import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { IntlShape } from 'react-intl';
import type { ReportDownloadProps } from 'components/table/ReportDownload';
import { ComparisonCell } from 'components/table/useDataEntriesInTables';

export type CellValueExporters = Record<string, (cellValue: unknown, intl: IntlShape) => string>;
export type HeaderExporters = Record<string, (intl: IntlShape) => string>;

export interface DataTableExportConfigOptions {
  exportName?: string;
  skippedColumns?: string[];
  customHeaderExporters?: HeaderExporters;
  customCellValueExporters?: CellValueExporters;
  reportDownloadProps?: ReportDownloadProps;
}

export const getComparisonCellExporterForMeasureByMetric = (
  unit: MeasureUnitCode
): ((cellValue: unknown, intl: IntlShape) => string) => {
  switch (unit) {
    case MeasureUnitCode.PERCENT:
    case MeasureUnitCode.RATE:
      return comparisonCellPercentageExporter;
    default:
      return comparisonCellNumberExporter;
  }
};

export const comparisonCellNumberExporter = (cellValue: unknown, intl: IntlShape): string => {
  const cellValues = cellValue as ComparisonCell;
  const valueToExport = cellValues.displayValue ?? cellValues.value;
  return intl.formatNumber(valueToExport, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const comparisonCellPercentageExporter = (cellValue: unknown, intl: IntlShape): string => {
  const cellValues = cellValue as ComparisonCell;
  const valueToExport = cellValues.displayValue ?? cellValues.value;
  return intl.formatNumber(valueToExport * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
};

export const integerCellExporter = (cellValue: unknown, intl: IntlShape) => {
  const value = cellValue as number;
  return intl.formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

export const numberCellExporter = (cellValue: unknown, intl: IntlShape) => {
  const value = cellValue as number;
  return intl.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
export const percentageCellExporter = (cellValue: unknown, intl: IntlShape) => {
  const value = cellValue as number;
  return intl.formatNumber(value * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
};

export const formattedMessageCellExporter = (cellValue: unknown, intl: IntlShape) => {
  const value = cellValue as string;
  return intl.formatMessage({ id: value });
};

export const formattedRegionCellExporter = (cellValue: unknown, intl: IntlShape) => {
  const value = cellValue as string;
  return intl.formatDisplayName(value, { type: 'region' }) ?? value;
};
